import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Users, Calendar, IndianRupee, ShieldAlert, Bell, UsersRound, CheckCircle2, Share2, Download, Copy, MessageCircle, Trash2, Pencil, Clock, XCircle, CheckCircle, Wallet, Send, AlertCircle, History, Crown, Swords, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { toPng } from 'html-to-image';

import { getApiUrl } from '@/lib/utils';
import { EditMatchModal } from '@/components/EditMatchModal';
import { CostBreakdownModal } from '@/components/CostBreakdownModal';

const API_URL = getApiUrl();

function PlayerPaymentsSummary() {
  const [paymentData, setPaymentData] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingWallet, setEditingWallet] = useState(null);
  const [newWalletBalance, setNewWalletBalance] = useState('');
  const [sortBy, setSortBy] = useState('dues');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, walletsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/player-payments`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/admin/player-wallets`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);
      setPaymentData(paymentsRes.data);
      setWallets(walletsRes.data);
    } catch (error) {
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWallet = async (userId) => {
    const newBalance = parseFloat(newWalletBalance);
    if (isNaN(newBalance) || newBalance < 0) {
      toast.error('Please enter a valid positive number');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/admin/wallet/${userId}`,
        { wallet_balance: newBalance },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Wallet balance updated');
      setEditingWallet(null);
      setNewWalletBalance('');
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wallet');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading...</div>;
  }

  const totalPending = paymentData.reduce((sum, p) => sum + p.total_pending, 0);
  const totalCollected = paymentData.reduce((sum, p) => sum + p.total_paid, 0);
  const totalWallets = wallets.reduce((sum, w) => sum + w.wallet_balance, 0);

  const getSortedPaymentData = () => {
    const sorted = [...paymentData];
    if (sortBy === 'dues') {
      sorted.sort((a, b) => b.total_pending - a.total_pending);
    } else if (sortBy === 'paid') {
      sorted.sort((a, b) => b.total_paid - a.total_paid);
    } else if (sortBy === 'name') {
      sorted.sort((a, b) => (a.user.nickname || a.user.name).localeCompare(b.user.nickname || b.user.name));
    }
    return sorted;
  };

  const sortedPaymentData = getSortedPaymentData();

  const handleExportDuesCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Pending Dues', 'Total Paid', 'Total Amount', 'Matches'];
    const rows = sortedPaymentData.map(p => [
      p.user.nickname || p.user.name,
      p.user.email || '',
      p.user.phone || '',
      p.total_pending.toFixed(0),
      p.total_paid.toFixed(0),
      p.total_amount.toFixed(0),
      p.payment_count
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `weakenders_dues_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Dues list exported!');
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <p className="text-xs text-slate-600 mb-1">Pending</p>
          <p className="text-xl font-bold text-slate-600">₹{totalPending.toFixed(0)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <p className="text-xs text-slate-600 mb-1">Collected</p>
          <p className="text-xl font-bold text-green-600">₹{totalCollected.toFixed(0)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-green-200 p-4">
          <p className="text-xs text-slate-600 mb-1">Wallets</p>
          <p className="text-xl font-bold text-green-600">₹{totalWallets.toFixed(0)}</p>
        </div>
      </div>

      {/* Player Wallets */}
      {wallets.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="w-5 h-5 text-green-600" />
            <h3 className="font-semibold text-green-800">Player Wallets</h3>
          </div>
          <div className="space-y-2">
            {wallets.map((item) => (
              <div
                key={item.user.user_id}
                className="flex items-center justify-between bg-white rounded-lg p-3"
                data-testid={`wallet-${item.user.user_id}`}
              >
                <div>
                  <p className="font-semibold text-[#0F172A]">{item.user.nickname || item.user.name}</p>
                  <p className="text-xs text-slate-500">Paid team expenses</p>
                </div>
                <div className="flex items-center gap-2">
                  {editingWallet === item.user.user_id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={newWalletBalance}
                        onChange={(e) => setNewWalletBalance(e.target.value)}
                        className="w-24 h-8 text-right"
                        placeholder="Amount"
                        data-testid={`wallet-input-${item.user.user_id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleUpdateWallet(item.user.user_id)}
                        className="h-8 bg-green-600 hover:bg-green-700"
                        data-testid={`wallet-save-${item.user.user_id}`}
                      >
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditingWallet(null); setNewWalletBalance(''); }}
                        className="h-8"
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-lg font-bold text-green-600">
                        ₹{item.wallet_balance.toFixed(0)}
                      </p>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-slate-400 hover:text-green-600"
                        onClick={() => {
                          setEditingWallet(item.user.user_id);
                          setNewWalletBalance(item.wallet_balance.toString());
                        }}
                        data-testid={`wallet-edit-${item.user.user_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Player Payment List */}
      {paymentData.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <IndianRupee className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No payment records yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-semibold text-[#0F172A]">Player Payments</h3>
            <div className="flex items-center gap-2">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-40 h-9" data-testid="payments-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dues">Sort: Pending Dues</SelectItem>
                  <SelectItem value="paid">Sort: Total Paid</SelectItem>
                  <SelectItem value="name">Sort: Name</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={handleExportDuesCSV}
                className="border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white h-9"
                data-testid="export-dues-csv-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                Export CSV
              </Button>
            </div>
          </div>
          {sortedPaymentData.map((playerData) => (
            <div
              key={playerData.user.user_id}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              data-testid={`player-payment-${playerData.user.user_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-bold text-[#0F172A] text-lg">{playerData.user.nickname || playerData.user.name}</p>
                  <p className="text-sm text-slate-600">{playerData.user.email}</p>
                  <p className="text-xs text-slate-500">{playerData.payment_count} matches</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#E63946]">₹{playerData.total_amount.toFixed(0)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-100">
                <div>
                  <p className="text-xs text-slate-500">Pending</p>
                  <p className="text-lg font-bold text-slate-600">₹{playerData.total_pending.toFixed(0)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Paid</p>
                  <p className="text-lg font-bold text-green-600">₹{playerData.total_paid.toFixed(0)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminSettings() {
  const [threshold, setThreshold] = useState('');
  const [savedThreshold, setSavedThreshold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      setThreshold(res.data.rsvp_dues_threshold.toString());
      setSavedThreshold(res.data.rsvp_dues_threshold);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const value = parseFloat(threshold);
    if (isNaN(value) || value < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/settings`,
        { rsvp_dues_threshold: value },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      setSavedThreshold(value);
      toast.success('Settings saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 space-y-4" data-testid="admin-settings-panel">
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-5 h-5 text-[#E63946]" />
        <h3 className="font-bold text-[#0F172A]">RSVP Dues Threshold</h3>
      </div>
      <p className="text-sm text-slate-600">
        Players with pending dues above this amount cannot RSVP "IN" for new matches. Currently: <span className="font-semibold">₹{savedThreshold}</span>
      </p>
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-[160px]">
          <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="number"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className="pl-8"
            data-testid="dues-threshold-input"
          />
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || parseFloat(threshold) === savedThreshold}
          className="bg-[#E63946] hover:bg-[#DC2626] text-white"
          data-testid="save-dues-threshold-btn"
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

function NotificationsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingRsvp, setSendingRsvp] = useState(false);
  const [sendingPayment, setSendingPayment] = useState(false);
  const [showSubscribers, setShowSubscribers] = useState(false);
  const [subscribers, setSubscribers] = useState(null);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications/stats`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      setStats(res.data);
    } catch (error) {
      toast.error('Failed to load notification stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    setLoadingSubscribers(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/notifications/subscribers`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      setSubscribers(res.data);
      setShowSubscribers(true);
    } catch (error) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const sendRsvpReminders = async () => {
    setSendingRsvp(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/notifications/rsvp-reminder`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      if (res.data.sent_count > 0) {
        toast.success(`RSVP reminders sent to ${res.data.sent_count} users`);
      } else {
        toast.info(res.data.message);
      }
      fetchStats();
    } catch (error) {
      toast.error('Failed to send RSVP reminders');
    } finally {
      setSendingRsvp(false);
    }
  };

  const sendPaymentReminders = async () => {
    setSendingPayment(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/notifications/payment-reminder`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      if (res.data.sent_count > 0) {
        toast.success(`Payment reminders sent to ${res.data.sent_count} users (₹${res.data.total_pending_amount.toFixed(0)} pending)`);
      } else {
        toast.info(res.data.message);
      }
      fetchStats();
    } catch (error) {
      toast.error('Failed to send payment reminders');
    } finally {
      setSendingPayment(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading...</div>;
  }

  // Subscribers modal/view
  if (showSubscribers) {
    // Calculate totals from the array response
    const subscribedUsers = subscribers?.filter(s => s.subscribed) || [];
    const totalDevices = subscribedUsers.reduce((sum, s) => sum + s.subscription_count, 0);
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#0F172A] flex items-center gap-2">
            <Users className="w-5 h-5 text-[#E63946]" />
            Push Notification Subscribers
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSubscribers(false)}
            data-testid="back-to-notify-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-600">
              {subscribedUsers.length} users • {totalDevices} devices
            </p>
          </div>

          {subscribedUsers.length > 0 ? (
            <div className="space-y-3">
              {subscribedUsers.map((sub) => {
                // Extract platforms from subscriptions
                const platforms = sub.subscriptions?.map(s => s.platform) || [];
                const hasIOS = platforms.some(p => p?.toLowerCase().includes('ios') || p?.toLowerCase().includes('safari'));
                const hasAndroid = platforms.some(p => p?.toLowerCase().includes('android') || p?.toLowerCase().includes('chrome'));
                const hasDesktop = platforms.some(p => p?.toLowerCase().includes('firefox') || p?.toLowerCase().includes('windows') || p === 'unknown');
                
                return (
                  <div
                    key={sub.user?.user_id}
                    className="flex items-center justify-between bg-slate-50 rounded-lg p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E63946] text-white flex items-center justify-center font-semibold text-sm">
                        {(sub.user?.name || 'U').substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-[#0F172A]">{sub.user?.nickname || sub.user?.name}</p>
                        <p className="text-xs text-slate-500">{sub.user?.cricket_role || 'Player'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 justify-end mb-1">
                        {hasIOS && (
                          <span className="px-2 py-0.5 bg-gray-800 text-white text-xs rounded-full">iOS</span>
                        )}
                        {hasAndroid && (
                          <span className="px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">Android</span>
                        )}
                        {hasDesktop && (
                          <span className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">Desktop</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">{sub.subscription_count} device{sub.subscription_count !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No subscribers yet
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Push Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#E63946]" />
            <h3 className="font-semibold text-[#0F172A]">Push Notification Stats</h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchSubscribers}
            disabled={loadingSubscribers}
            data-testid="view-subscribers-btn"
          >
            {loadingSubscribers ? (
              <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Users className="w-4 h-4 mr-2" />
            )}
            View Subscribers
          </Button>
        </div>
        
        {/* Summary stats */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500">Users Subscribed</p>
            <p className="text-2xl font-bold text-[#0F172A]">{stats?.unique_subscribed_users || 0}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500">Total Devices</p>
            <p className="text-2xl font-bold text-[#0F172A]">{stats?.total_subscriptions || 0}</p>
          </div>
        </div>

        {/* Platform breakdown */}
        {stats?.platforms && (
          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs text-slate-500 mb-2">By Platform</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-gray-800"></span>
                <span className="text-sm text-slate-700">iOS: {stats.platforms.apple || 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-green-600"></span>
                <span className="text-sm text-slate-700">Android: {stats.platforms.google || 0}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-blue-600"></span>
                <span className="text-sm text-slate-700">Desktop: {(stats.platforms.mozilla || 0) + (stats.platforms.other || 0)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RSVP Reminders */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-[#0F172A]">RSVP Reminders</h3>
          </div>
          <Button
            onClick={sendRsvpReminders}
            disabled={sendingRsvp || !stats?.rsvp_pending?.length}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="send-rsvp-reminder-btn"
          >
            {sendingRsvp ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Reminders
          </Button>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Send push notifications to users who haven't RSVP'd for upcoming Sunday matches.
        </p>
        
        {stats?.rsvp_pending?.length > 0 ? (
          <div className="space-y-2">
            {stats.rsvp_pending.map((match) => (
              <div
                key={match.match_id}
                className="flex items-center justify-between bg-blue-50 rounded-lg p-3"
              >
                <div>
                  <p className="font-medium text-blue-900">{match.date}</p>
                  <p className="text-xs text-blue-700">{match.rsvp_count} voted</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-600">{match.pending_count}</p>
                  <p className="text-xs text-blue-700">pending</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-slate-50 rounded-lg p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-slate-600">No upcoming Sunday matches</p>
          </div>
        )}
      </div>

      {/* Payment Reminders */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-[#0F172A]">Payment Reminders</h3>
          </div>
          <Button
            onClick={sendPaymentReminders}
            disabled={sendingPayment || !stats?.payment_stats?.users_with_dues}
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="send-payment-reminder-btn"
          >
            {sendingPayment ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Send Reminders
          </Button>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Send push notifications to all users with pending payments.
        </p>
        
        {stats?.payment_stats?.users_with_dues > 0 ? (
          <div className="bg-amber-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-800">Users with pending dues</p>
                <p className="text-2xl font-bold text-amber-600">{stats.payment_stats.users_with_dues}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-amber-800">Total pending</p>
                <p className="text-2xl font-bold text-amber-600">₹{stats.payment_stats.total_pending.toFixed(0)}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
            <p className="text-slate-600">All payments cleared!</p>
          </div>
        )}
      </div>

      {/* Info Note */}
      <div className="bg-slate-100 rounded-lg p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-slate-600">
          Notifications are only sent to users who have enabled push notifications on their device. 
          Users need to install the app (Add to Home Screen) and grant notification permissions.
        </p>
      </div>
    </div>
  );
}

function PaymentVerification({ onVerified }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingVerifications();
  }, []);

  const fetchPendingVerifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payments-pending-verification`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      setPayments(res.data);
    } catch (error) {
      toast.error('Failed to load pending verifications');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (paymentId, approved) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/verify-payment/${paymentId}`,
        { approved },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(approved ? 'Payment verified!' : 'Payment rejected');
      // Remove from list
      setPayments(payments.filter(p => p.payment_id !== paymentId));
      onVerified?.();
    } catch (error) {
      toast.error('Failed to verify payment');
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading...</div>;
  }

  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <p className="text-slate-600 font-semibold">All caught up!</p>
        <p className="text-sm text-slate-500 mt-1">No payments pending verification</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-yellow-600" />
          <p className="font-semibold text-yellow-800">
            {payments.length} payment{payments.length !== 1 ? 's' : ''} awaiting verification
          </p>
        </div>
      </div>

      {/* Payment Cards */}
      <div className="space-y-3">
        {payments.map((payment) => (
          <div
            key={payment.payment_id}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
            data-testid={`verify-payment-${payment.payment_id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-bold text-[#0F172A] text-lg">
                  {payment.user?.nickname || payment.user?.name || 'Unknown Player'}
                </p>
                <p className="text-sm text-slate-600">{payment.user?.email}</p>
              </div>
              <p className="text-2xl font-bold text-[#E63946]">₹{payment.amount.toFixed(0)}</p>
            </div>

            {/* Match Info */}
            <div className="bg-slate-50 rounded-lg p-3 mb-3">
              <p className="text-sm font-semibold text-slate-700">
                {payment.match?.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
              </p>
              <p className="text-xs text-slate-500">
                {payment.match?.date && format(new Date(payment.match.date), 'dd MMM yyyy')}
                {payment.match?.venue && ` • ${payment.match.venue}`}
              </p>
            </div>

            {/* Transaction Reference */}
            {payment.transaction_ref && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-600 font-semibold">Transaction Ref:</p>
                <p className="text-sm text-blue-800 font-mono">{payment.transaction_ref}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 pt-3 border-t border-slate-100">
              <Button
                onClick={() => handleVerify(payment.payment_id, false)}
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                data-testid="reject-payment-btn"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button
                onClick={() => handleVerify(payment.payment_id, true)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="approve-payment-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Verify
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ManualPaymentVerification({ onVerified }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifyingId, setVerifyingId] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [verifyNote, setVerifyNote] = useState('');

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/all-pending-payments`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      setPayments(res.data);
    } catch (error) {
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const openVerifyDialog = (payment) => {
    setSelectedPayment(payment);
    setVerifyNote('');
    setNoteDialogOpen(true);
  };

  const handleManualVerify = async () => {
    if (!selectedPayment) return;
    
    setVerifyingId(selectedPayment.payment_id);
    try {
      await axios.put(
        `${API_URL}/api/admin/manual-verify-payment/${selectedPayment.payment_id}`,
        { note: verifyNote || 'Cash/Manual payment' },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Payment marked as verified!');
      setPayments(payments.filter(p => p.payment_id !== selectedPayment.payment_id));
      setNoteDialogOpen(false);
      setSelectedPayment(null);
      onVerified?.();
    } catch (error) {
      toast.error('Failed to verify payment');
    } finally {
      setVerifyingId(null);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading...</div>;
  }

  if (payments.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <p className="text-slate-600 font-semibold">No pending payments!</p>
        <p className="text-sm text-slate-500 mt-1">All player dues are cleared</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <IndianRupee className="w-5 h-5 text-blue-600" />
          <div>
            <p className="font-semibold text-blue-800">
              {payments.length} pending payment{payments.length !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-blue-600">Use this to mark cash/direct payments as verified</p>
          </div>
        </div>
      </div>

      {/* Payment Cards */}
      <div className="space-y-3">
        {payments.map((payment) => (
          <div
            key={payment.payment_id}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
            data-testid={`manual-verify-${payment.payment_id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-bold text-[#0F172A] text-lg">
                  {payment.user?.nickname || payment.user?.name || 'Unknown Player'}
                </p>
                <p className="text-sm text-slate-600">{payment.user?.email}</p>
              </div>
              <p className="text-2xl font-bold text-[#E63946]">₹{payment.amount.toFixed(0)}</p>
            </div>

            {/* Match Info */}
            <div className="bg-slate-50 rounded-lg p-3 mb-3">
              <p className="text-sm font-semibold text-slate-700">
                {payment.match?.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
              </p>
              <p className="text-xs text-slate-500">
                {payment.match?.date && format(new Date(payment.match.date), 'dd MMM yyyy')}
                {payment.match?.venue && ` • ${payment.match.venue}`}
              </p>
            </div>

            {/* Action Button */}
            <Button
              onClick={() => openVerifyDialog(payment)}
              disabled={verifyingId === payment.payment_id}
              className="w-full bg-[#E63946] hover:bg-[#DC2626] text-white"
              data-testid="manual-verify-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {verifyingId === payment.payment_id ? 'Verifying...' : 'Mark as Paid'}
            </Button>
          </div>
        ))}
      </div>

      {/* Verify Confirmation Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Manual Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedPayment && (
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-semibold text-[#0F172A]">
                  {selectedPayment.user?.nickname || selectedPayment.user?.name}
                </p>
                <p className="text-2xl font-bold text-[#E63946]">₹{selectedPayment.amount.toFixed(0)}</p>
              </div>
            )}
            <div>
              <Label htmlFor="verify-note">Payment Note (Optional)</Label>
              <Input
                id="verify-note"
                placeholder="e.g., Cash received, Bank transfer, etc."
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
                data-testid="verify-note-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setNoteDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleManualVerify}
                disabled={verifyingId}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-manual-verify-btn"
              >
                {verifyingId ? 'Verifying...' : 'Confirm Payment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminPanel() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [matches, setMatches] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createMatchOpen, setCreateMatchOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [userRes, matchesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/matches`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/users/all`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);

      if (!userRes.data.is_admin) {
        toast.error('Admin access required');
        navigate('/dashboard');
        return;
      }

      setUser(userRes.data);
      setMatches(matchesRes.data);
      setAllUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load admin panel');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#0F172A] text-white pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2 flex-1">
            <ShieldAlert className="w-5 h-5" />
            <h1 className="text-lg font-bold">ADMIN PANEL</h1>
          </div>
          <Button
            onClick={() => navigate('/admin/notifications')}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            data-testid="notifications-btn"
          >
            <Bell className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Tabs defaultValue="matches" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 bg-white rounded-lg p-1 border border-slate-200">
            <TabsTrigger value="matches" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white text-xs sm:text-sm" data-testid="matches-tab">
              <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Matches</span>
              <span className="sm:hidden">Match</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white text-xs sm:text-sm" data-testid="payments-tab">
              <IndianRupee className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Payments</span>
              <span className="sm:hidden">Pay</span>
            </TabsTrigger>
            <TabsTrigger value="notify" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white text-xs sm:text-sm" data-testid="notifications-tab">
              <Bell className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Notify</span>
              <span className="sm:hidden">Notify</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white text-xs sm:text-sm" data-testid="users-tab">
              <Users className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Users</span>
              <span className="sm:hidden">Users</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#E63946] data-[state=active]:text-white text-xs sm:text-sm" data-testid="settings-tab">
              <Settings className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Settings</span>
              <span className="sm:hidden">Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="matches" className="space-y-4">
            <Dialog open={createMatchOpen} onOpenChange={setCreateMatchOpen}>
              <DialogTrigger asChild>
                <Button
                  className="w-full bg-[#E63946] hover:bg-[#DC2626] text-white font-bold uppercase py-6"
                  data-testid="create-match-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create New Match
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Match</DialogTitle>
                </DialogHeader>
                <CreateMatchForm onSuccess={() => { setCreateMatchOpen(false); fetchData(); }} />
              </DialogContent>
            </Dialog>

            <Tabs defaultValue="upcoming" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2 bg-slate-100 rounded-lg p-1">
                <TabsTrigger 
                  value="upcoming" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-2"
                  data-testid="upcoming-matches-tab"
                >
                  <Calendar className="w-4 h-4" />
                  <span>Upcoming</span>
                  {matches.filter(m => new Date(m.date) >= new Date(new Date().setHours(0,0,0,0))).length > 0 && (
                    <span className="bg-[#E63946] text-white text-xs px-2 py-0.5 rounded-full">
                      {matches.filter(m => new Date(m.date) >= new Date(new Date().setHours(0,0,0,0))).length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger 
                  value="past" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-2"
                  data-testid="past-matches-tab"
                >
                  <History className="w-4 h-4" />
                  <span>Past Games</span>
                  {matches.filter(m => new Date(m.date) < new Date(new Date().setHours(0,0,0,0))).length > 0 && (
                    <span className="bg-slate-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {matches.filter(m => new Date(m.date) < new Date(new Date().setHours(0,0,0,0))).length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upcoming" className="space-y-3">
                {matches
                  .filter(m => new Date(m.date) >= new Date(new Date().setHours(0,0,0,0)))
                  .sort((a, b) => new Date(a.date) - new Date(b.date))
                  .length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                      <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-semibold">No upcoming matches</p>
                      <p className="text-sm text-slate-500 mt-1">Create a new match to get started</p>
                    </div>
                  ) : (
                    matches
                      .filter(m => new Date(m.date) >= new Date(new Date().setHours(0,0,0,0)))
                      .sort((a, b) => new Date(a.date) - new Date(b.date))
                      .map((match) => (
                        <AdminMatchCard key={match.match_id} match={match} onUpdate={fetchData} />
                      ))
                  )}
              </TabsContent>

              <TabsContent value="past" className="space-y-3">
                {matches
                  .filter(m => new Date(m.date) < new Date(new Date().setHours(0,0,0,0)))
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .length === 0 ? (
                    <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                      <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-600 font-semibold">No past games yet</p>
                      <p className="text-sm text-slate-500 mt-1">Completed matches will appear here</p>
                    </div>
                  ) : (
                    matches
                      .filter(m => new Date(m.date) < new Date(new Date().setHours(0,0,0,0)))
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((match) => (
                        <AdminMatchCard key={match.match_id} match={match} onUpdate={fetchData} />
                      ))
                  )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="payments" className="space-y-3">
            <Tabs defaultValue="verify" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3 bg-slate-100 rounded-lg p-1">
                <TabsTrigger 
                  value="verify" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                  data-testid="verify-payments-subtab"
                >
                  <Clock className="w-4 h-4 mr-1 sm:mr-2" />
                  Verify
                </TabsTrigger>
                <TabsTrigger 
                  value="manual" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                  data-testid="manual-payments-subtab"
                >
                  <CheckCircle className="w-4 h-4 mr-1 sm:mr-2" />
                  Manual
                </TabsTrigger>
                <TabsTrigger 
                  value="summary" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm"
                  data-testid="summary-subtab"
                >
                  <Users className="w-4 h-4 mr-1 sm:mr-2" />
                  Summary
                </TabsTrigger>
              </TabsList>

              <TabsContent value="verify">
                <PaymentVerification onVerified={fetchData} />
              </TabsContent>

              <TabsContent value="manual">
                <ManualPaymentVerification onVerified={fetchData} />
              </TabsContent>

              <TabsContent value="summary">
                <PlayerPaymentsSummary />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="users" className="space-y-3">
            {allUsers.map((u) => (
              <UserCard key={u.user_id} user={u} onUpdate={fetchData} />
            ))}
          </TabsContent>

          <TabsContent value="notify" className="space-y-3">
            <NotificationsPanel />
          </TabsContent>

          <TabsContent value="settings" className="space-y-3">
            <AdminSettings />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function CreateMatchForm({ onSuccess }) {
  const [formData, setFormData] = useState({
    match_type: '',
    date: '',
    venue: '',
    opponent: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.match_type) {
      toast.error('Please select match type');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/matches`, formData, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      toast.success('Match created!');
      onSuccess();
    } catch (error) {
      toast.error('Failed to create match');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-match-form">
      <div>
        <Label htmlFor="match_type">Match Type</Label>
        <Select value={formData.match_type} onValueChange={(value) => setFormData({ ...formData, match_type: value, opponent: value === 'sunday' ? '' : formData.opponent })}>
          <SelectTrigger data-testid="match-type-select">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="saturday" data-testid="type-saturday">Saturday (External - 13 players)</SelectItem>
            <SelectItem value="sunday" data-testid="type-sunday">Sunday (Internal - 24 players)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="date">Date</Label>
        <Input
          id="date"
          type="date"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          required
          data-testid="match-date-input"
        />
      </div>

      <div>
        <Label htmlFor="venue">Venue (Optional)</Label>
        <Input
          id="venue"
          type="text"
          placeholder="Match venue"
          value={formData.venue}
          onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
          data-testid="match-venue-input"
        />
      </div>

      {formData.match_type === 'saturday' && (
        <div>
          <Label htmlFor="opponent">Opponent Team</Label>
          <Input
            id="opponent"
            type="text"
            placeholder="e.g., Sunday Warriors CC"
            value={formData.opponent}
            onChange={(e) => setFormData({ ...formData, opponent: e.target.value })}
            data-testid="match-opponent-input"
          />
        </div>
      )}

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-[#E63946] hover:bg-[#DC2626] text-white"
        data-testid="submit-match-btn"
      >
        {loading ? 'Creating...' : 'Create Match'}
      </Button>
    </form>
  );
}

function AdminMatchCard({ match, onUpdate }) {
  const navigate = useNavigate();
  const teamSheetRef = useRef(null);
  const [costBreakdownOpen, setCostBreakdownOpen] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [editMatchOpen, setEditMatchOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [captainDialogOpen, setCaptainDialogOpen] = useState(false);
  const [captainA, setCaptainA] = useState(match.captains?.captain_a || '');
  const [captainB, setCaptainB] = useState(match.captains?.captain_b || '');
  const [savingCaptains, setSavingCaptains] = useState(false);
  const [players, setPlayers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showTeamSheet, setShowTeamSheet] = useState(false);

  // Fetch players for captain selection
  useEffect(() => {
    if (captainDialogOpen && match.selected_players?.length > 0) {
      fetchPlayers();
    }
  }, [captainDialogOpen, match.selected_players]);

  // Fetch all users for team sheet generation
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/users/all`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        });
        setAllUsers(res.data);
      } catch (error) {
        console.error('Failed to fetch users');
      }
    };
    fetchAllUsers();
  }, []);

  const fetchPlayers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
      });
      const selectedPlayers = res.data.filter(u => match.selected_players?.includes(u.user_id));
      setPlayers(selectedPlayers);
    } catch (error) {
      console.error('Failed to fetch players');
    }
  };

  const handleSaveCaptains = async () => {
    if (!captainA || !captainB) {
      toast.error('Please select both captains');
      return;
    }
    if (captainA === captainB) {
      toast.error('Captains must be different players');
      return;
    }
    setSavingCaptains(true);
    try {
      await axios.put(
        `${API_URL}/api/matches/${match.match_id}/captains`,
        { captain_a: captainA, captain_b: captainB },
        { headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } }
      );
      toast.success('Captains assigned! Selection Arena is now available.');
      setCaptainDialogOpen(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign captains');
    } finally {
      setSavingCaptains(false);
    }
  };

  const handleSendRsvpReminder = async () => {
    setNotifyLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/matches/${match.match_id}/send-rsvp-reminder`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      if (res.data.sent_count > 0) {
        toast.success(`RSVP reminder sent to ${res.data.sent_count} users`);
      } else {
        toast.info(res.data.message);
      }
    } catch (error) {
      toast.error('Failed to send RSVP reminder');
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleDeleteMatch = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/matches/${match.match_id}`,
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Match deleted successfully');
      onUpdate();
    } catch (error) {
      toast.error('Failed to delete match');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getTeamSheetDataUrl = async () => {
    // Show the hidden team sheet container
    setShowTeamSheet(true);
    
    // Wait for render
    await new Promise(resolve => setTimeout(resolve, 100));
    
    if (!teamSheetRef.current) {
      throw new Error('Team sheet container not found');
    }

    try {
      const dataUrl = await toPng(teamSheetRef.current, {
        quality: 1,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });
      return dataUrl;
    } finally {
      setShowTeamSheet(false);
    }
  };

  const handleDownloadTeamSheet = async () => {
    setShareLoading(true);
    try {
      const dataUrl = await getTeamSheetDataUrl();
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `teamsheet_${match.match_type}_${match.date}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Team sheet downloaded!');
    } catch (error) {
      console.error('Error generating team sheet:', error);
      toast.error('Failed to generate team sheet');
    } finally {
      setShareLoading(false);
      setShareMenuOpen(false);
    }
  };

  const handleCopyToClipboard = async () => {
    setShareLoading(true);
    try {
      const dataUrl = await getTeamSheetDataUrl();
      
      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      
      // Use Clipboard API to copy image
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({ 'image/png': blob })
        ]);
        toast.success('Team sheet copied to clipboard!');
      } else {
        // Fallback: download instead
        toast.error('Clipboard not supported. Downloading instead...');
        await handleDownloadTeamSheet();
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy. Try downloading instead.');
    } finally {
      setShareLoading(false);
      setShareMenuOpen(false);
    }
  };

  const handleShareWhatsApp = async () => {
    setShareLoading(true);
    try {
      // First download the image
      const dataUrl = await getTeamSheetDataUrl();
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `teamsheet_${match.match_type}_${match.date}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Format match info for WhatsApp
      const matchDate = format(new Date(match.date), 'EEEE, dd MMM yyyy');
      const opponent = match.opponent ? ` vs ${match.opponent}` : '';
      const teamType = match.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Showdown';
      
      let message = `🏏 *THE WEAKENDERS*\n\n`;
      message += `📅 *${teamType}*${opponent}\n`;
      message += `📆 ${matchDate}\n`;
      if (match.venue) message += `📍 ${match.venue}\n`;
      message += `\n👥 Team sheet attached!`;
      
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('Team sheet downloaded! Attach it in WhatsApp.');
    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      toast.error('Failed to prepare for sharing');
    } finally {
      setShareLoading(false);
      setShareMenuOpen(false);
    }
  };

  // Check if team sheet can be generated
  const canGenerateTeamSheet = match.match_type === 'saturday' 
    ? match.selected_players?.length > 0 
    : (match.team_a?.length > 0 || match.team_b?.length > 0);

  // Get player data for team sheet
  const getSelectedPlayers = () => {
    return (match.selected_players || []).map(pid => allUsers.find(u => u.user_id === pid)).filter(Boolean);
  };
  
  const getTeamAPlayers = () => {
    return (match.team_a || []).map(pid => allUsers.find(u => u.user_id === pid)).filter(Boolean);
  };
  
  const getTeamBPlayers = () => {
    return (match.team_b || []).map(pid => allUsers.find(u => u.user_id === pid)).filter(Boolean);
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getRoleDisplay = (role, short = false) => {
    const roleLower = (role || '').toLowerCase();
    if (short) {
      if (roleLower.includes('keep')) return 'WK';
      if (roleLower.includes('bowling') && roleLower.includes('allrounder')) return 'BOWL AR';
      if (roleLower.includes('batting') && roleLower.includes('allrounder')) return 'BAT AR';
      if (roleLower.includes('allrounder')) return 'AR';
      if (roleLower.includes('bowl')) return 'BOWLER';
      return 'BATTER';
    }
    if (roleLower.includes('keep')) return 'WICKET KEEPER';
    if (roleLower.includes('bowling') && roleLower.includes('allrounder')) return 'BOWLING ALL-ROUNDER';
    if (roleLower.includes('batting') && roleLower.includes('allrounder')) return 'BATTING ALL-ROUNDER';
    if (roleLower.includes('allrounder')) return 'ALL-ROUNDER';
    if (roleLower.includes('bowl')) return 'BOWLER';
    return 'BATTER';
  };

  return (
    <>
    {/* Hidden Team Sheet for Client-Side Generation */}
    {showTeamSheet && (
      <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
        <div ref={teamSheetRef} style={{ fontFamily: "'Inter', -apple-system, sans-serif", background: 'white' }}>
          {match.match_type === 'saturday' ? (
            // Saturday Team Sheet
            <div style={{ width: '800px', border: '12px solid rgba(220, 38, 38, 0.05)' }}>
              <div style={{ textAlign: 'center', padding: '30px 40px 20px' }}>
                <img src="/images/weakenders-logo.png" style={{ width: '100px', height: '100px', objectFit: 'contain', marginBottom: '15px' }} alt="Logo" />
                <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '36px', color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 8px 0' }}>Weakenders</h1>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#DC2626', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 600, margin: 0 }}>Official Team Sheet</p>
              </div>
              <div style={{ display: 'flex', background: '#F8FAFC', borderTop: '1px solid rgba(220, 38, 38, 0.1)', borderBottom: '1px solid rgba(220, 38, 38, 0.1)' }}>
                <div style={{ flex: 1, textAlign: 'center', padding: '18px 15px', borderRight: '1px solid rgba(220, 38, 38, 0.1)' }}>
                  <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#DC2626', fontWeight: 700, margin: '0 0 4px 0' }}>Date</p>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '16px', fontWeight: 600, color: '#0F172A', margin: 0 }}>{formatDate(match.date)}</p>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '18px 15px', borderRight: '1px solid rgba(220, 38, 38, 0.1)' }}>
                  <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#DC2626', fontWeight: 700, margin: '0 0 4px 0' }}>Venue</p>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '16px', fontWeight: 600, color: '#0F172A', margin: 0 }}>{match.venue || 'TBD'}</p>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '18px 15px' }}>
                  <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#DC2626', fontWeight: 700, margin: '0 0 4px 0' }}>Opponent</p>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '16px', fontWeight: 600, color: '#0F172A', margin: 0 }}>{match.opponent || 'TBD'}</p>
                </div>
              </div>
              <div style={{ padding: '25px 30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid rgba(220, 38, 38, 0.2)', paddingBottom: '10px', marginBottom: '15px' }}>
                  <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', fontWeight: 700, fontStyle: 'italic', color: '#0F172A', margin: 0 }}>The Playing XI</h2>
                </div>
                {getSelectedPlayers().slice(0, 13).map((player, idx) => (
                  <div key={player.user_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid rgba(220, 38, 38, 0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: 'rgba(220, 38, 38, 0.4)' }}>{String(idx + 1).padStart(2, '0')}</span>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <p style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A', margin: 0 }}>{player.nickname || player.name}</p>
                        <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', fontStyle: 'italic', margin: 0 }}>{getRoleDisplay(player.cricket_role)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background: '#F8FAFC', textAlign: 'center', padding: '15px', borderTop: '1px solid rgba(220, 38, 38, 0.1)' }}>
                <p style={{ fontSize: '11px', color: '#64748B', letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>Weakenders Cricket Club</p>
              </div>
            </div>
          ) : (
            // Sunday Team Sheet
            <div style={{ width: '900px', border: '12px solid rgba(220, 38, 38, 0.05)' }}>
              <div style={{ textAlign: 'center', padding: '25px 40px 15px' }}>
                <img src="/images/weakenders-logo.png" style={{ width: '80px', height: '80px', objectFit: 'contain', marginBottom: '10px' }} alt="Logo" />
                <h1 style={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '32px', color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 6px 0' }}>Weakenders</h1>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: '#DC2626', textTransform: 'uppercase', letterSpacing: '0.2em', fontWeight: 600, margin: 0 }}>Sunday Showdown</p>
              </div>
              <div style={{ display: 'flex', background: '#F8FAFC', borderTop: '1px solid rgba(220, 38, 38, 0.1)', borderBottom: '1px solid rgba(220, 38, 38, 0.1)' }}>
                <div style={{ flex: 1, textAlign: 'center', padding: '14px 15px', borderRight: '1px solid rgba(220, 38, 38, 0.1)' }}>
                  <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#DC2626', fontWeight: 700, margin: '0 0 3px 0' }}>Date</p>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '14px', fontWeight: 600, color: '#0F172A', margin: 0 }}>{formatDate(match.date)}</p>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '14px 15px', borderRight: '1px solid rgba(220, 38, 38, 0.1)' }}>
                  <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#DC2626', fontWeight: 700, margin: '0 0 3px 0' }}>Venue</p>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '14px', fontWeight: 600, color: '#0F172A', margin: 0 }}>{match.venue || 'TBD'}</p>
                </div>
                <div style={{ flex: 1, textAlign: 'center', padding: '14px 15px' }}>
                  <p style={{ fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.15em', color: '#DC2626', fontWeight: 700, margin: '0 0 3px 0' }}>Match</p>
                  <p style={{ fontFamily: 'Georgia, serif', fontSize: '14px', fontWeight: 600, color: '#0F172A', margin: 0 }}>Internal Game</p>
                </div>
              </div>
              <div style={{ textAlign: 'center', padding: '15px 20px', background: 'linear-gradient(90deg, #DC2626 0%, #0F172A 50%, #2563EB 100%)' }}>
                <p style={{ fontFamily: 'Georgia, serif', fontSize: '18px', fontWeight: 700, color: 'white', letterSpacing: '0.05em', margin: 0 }}>Kacche Khiladi vs Bhandball</p>
              </div>
              <div style={{ display: 'flex', padding: '20px', gap: '20px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ textAlign: 'center', paddingBottom: '10px', marginBottom: '10px', borderBottom: '3px solid #DC2626' }}>
                    <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '16px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#DC2626', margin: 0 }}>Bhandball</h3>
                  </div>
                  {getTeamAPlayers().slice(0, 12).map((player, idx) => (
                    <div key={player.user_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 8px', borderBottom: '1px solid rgba(0, 0, 0, 0.03)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(100, 116, 139, 0.5)', minWidth: '20px' }}>{String(idx + 1).padStart(2, '0')}</span>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', margin: 0 }}>{player.nickname || player.name}</p>
                        <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#64748B', fontStyle: 'italic', margin: 0 }}>{getRoleDisplay(player.cricket_role, true)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ textAlign: 'center', paddingBottom: '10px', marginBottom: '10px', borderBottom: '3px solid #2563EB' }}>
                    <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '16px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#2563EB', margin: 0 }}>Kacche Khiladi</h3>
                  </div>
                  {getTeamBPlayers().slice(0, 12).map((player, idx) => (
                    <div key={player.user_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '6px 8px', borderBottom: '1px solid rgba(0, 0, 0, 0.03)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(100, 116, 139, 0.5)', minWidth: '20px' }}>{String(idx + 1).padStart(2, '0')}</span>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#0F172A', margin: 0 }}>{player.nickname || player.name}</p>
                        <p style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.03em', color: '#64748B', fontStyle: 'italic', margin: 0 }}>{getRoleDisplay(player.cricket_role, true)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ background: '#F8FAFC', textAlign: 'center', padding: '12px', borderTop: '1px solid rgba(220, 38, 38, 0.1)' }}>
                <p style={{ fontSize: '10px', color: '#64748B', letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>Weakenders Cricket Club</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-testid={`admin-match-${match.match_id}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A] uppercase">
            {match.match_type === 'saturday' ? 'Saturday Match' : 'Sunday Match'}
          </h3>
          <p className="text-sm text-slate-600">
            {format(new Date(match.date), 'EEEE, dd MMM yyyy')}
          </p>
          {match.venue && <p className="text-sm text-slate-500">{match.venue}</p>}
          {match.opponent && (
            <p className="text-sm font-semibold text-[#E63946] mt-1">vs {match.opponent}</p>
          )}
        </div>
        <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-full">
          {match.selected_players?.length || 0}/{match.max_players}
        </span>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Button
          size="sm"
          onClick={() => navigate(`/match/${match.match_id}`)}
          className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
          data-testid="view-match-btn"
        >
          View Match
        </Button>

        <Button
          size="sm"
          onClick={handleSendRsvpReminder}
          disabled={notifyLoading}
          variant="outline"
          className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white"
          data-testid="send-rsvp-reminder-btn"
        >
          {notifyLoading ? (
            <div className="w-4 h-4 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mr-1" />
          ) : (
            <Bell className="w-4 h-4 mr-1" />
          )}
          Notify
        </Button>

        {match.match_type === 'saturday' && (
          <Button
            size="sm"
            onClick={() => navigate(`/admin/select-saturday/${match.match_id}`)}
            variant="outline"
            className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
            data-testid="select-saturday-btn"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Select Team (13)
          </Button>
        )}

        {match.match_type === 'sunday' && match.selected_players?.length > 0 && (
          <Button
            size="sm"
            onClick={() => navigate(`/admin/allocate-teams/${match.match_id}`)}
            variant="outline"
            className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
            data-testid="allocate-teams-btn"
          >
            <UsersRound className="w-4 h-4 mr-1" />
            Allocate Teams
          </Button>
        )}

        {/* Captain Selection for Sunday matches */}
        {match.match_type === 'sunday' && match.selected_players?.length >= 2 && (
          <>
            <Button
              size="sm"
              onClick={() => setCaptainDialogOpen(true)}
              variant="outline"
              className={`border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white ${match.captains ? 'bg-amber-50' : ''}`}
              data-testid="set-captains-btn"
            >
              <Crown className="w-4 h-4 mr-1" />
              {match.captains ? 'Edit Captains' : 'Set Captains'}
            </Button>

            {match.captains && (
              <Button
                size="sm"
                onClick={() => navigate(`/arena/${match.match_id}`)}
                variant="outline"
                className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white"
                data-testid="view-arena-btn"
              >
                <Swords className="w-4 h-4 mr-1" />
                View Arena
              </Button>
            )}

            <Dialog open={captainDialogOpen} onOpenChange={setCaptainDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    Select Team Captains
                  </DialogTitle>
                  <DialogDescription>
                    Choose two captains from the selected players. They will pick teams alternately in the Selection Arena.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-red-600 flex items-center gap-2">
                      <Crown className="w-4 h-4" /> Captain A (Team Red)
                    </Label>
                    <Select value={captainA} onValueChange={setCaptainA}>
                      <SelectTrigger data-testid="captain-a-select">
                        <SelectValue placeholder="Select Captain A" />
                      </SelectTrigger>
                      <SelectContent>
                        {players.filter(p => p.user_id !== captainB).map(player => (
                          <SelectItem key={player.user_id} value={player.user_id}>
                            {player.nickname || player.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-blue-600 flex items-center gap-2">
                      <Crown className="w-4 h-4" /> Captain B (Team Blue)
                    </Label>
                    <Select value={captainB} onValueChange={setCaptainB}>
                      <SelectTrigger data-testid="captain-b-select">
                        <SelectValue placeholder="Select Captain B" />
                      </SelectTrigger>
                      <SelectContent>
                        {players.filter(p => p.user_id !== captainA).map(player => (
                          <SelectItem key={player.user_id} value={player.user_id}>
                            {player.nickname || player.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCaptainDialogOpen(false)}>Cancel</Button>
                  <Button 
                    onClick={handleSaveCaptains} 
                    disabled={savingCaptains || !captainA || !captainB}
                    className="bg-amber-500 hover:bg-amber-600"
                  >
                    {savingCaptains ? 'Saving...' : 'Assign Captains'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}

        {canGenerateTeamSheet && (
          <DropdownMenu open={shareMenuOpen} onOpenChange={setShareMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                disabled={shareLoading}
                variant="outline"
                className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white"
                data-testid="share-team-btn"
              >
                <Share2 className="w-4 h-4 mr-1" />
                {shareLoading ? 'Loading...' : 'Share Team'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={handleDownloadTeamSheet}
                className="cursor-pointer"
                data-testid="download-teamsheet-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PNG
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={handleCopyToClipboard}
                className="cursor-pointer"
                data-testid="copy-teamsheet-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy to Clipboard
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={handleShareWhatsApp}
                className="cursor-pointer text-green-600"
                data-testid="whatsapp-teamsheet-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Share to WhatsApp
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => setCostBreakdownOpen(true)}
          className="border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white" 
          data-testid="manage-cost-btn"
        >
          <IndianRupee className="w-4 h-4 mr-1" />
          {match.cost ? 'Update' : 'Set'} Cost
        </Button>

        <CostBreakdownModal
          match={match}
          isOpen={costBreakdownOpen}
          onClose={() => setCostBreakdownOpen(false)}
          onSaved={onUpdate}
        />

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="border-slate-300 text-slate-500 hover:bg-red-50 hover:border-red-300 hover:text-red-600"
              data-testid="delete-match-btn"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Match?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this {match.match_type} match on {format(new Date(match.date), 'dd MMM yyyy')}, 
                along with all RSVPs and payment records. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteMatch}
                disabled={deleteLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Match'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          size="sm"
          variant="outline"
          onClick={() => setEditMatchOpen(true)}
          className="border-slate-300 text-slate-600 hover:bg-slate-100"
          data-testid="edit-match-btn"
        >
          <Pencil className="w-4 h-4" />
        </Button>
      </div>

      {/* Edit Match Modal */}
      {editMatchOpen && (
        <EditMatchModal
          match={match}
          onClose={() => setEditMatchOpen(false)}
          onSaved={onUpdate}
        />
      )}

      {match.cost && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm font-semibold text-green-900">
            Total: ₹{match.cost} | Per Player: ₹{(match.cost / (match.selected_players?.length || 1)).toFixed(0)}
          </p>
        </div>
      )}
    </div>
    </>
  );
}

function UserCard({ user, onUpdate }) {
  const [loading, setLoading] = useState(false);

  const toggleAdmin = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API_URL}/api/users/${user.user_id}/admin`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(`${user.name} admin status updated`);
      onUpdate();
    } catch (error) {
      toast.error('Failed to update admin status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex items-center justify-between" data-testid={`user-${user.user_id}`}>
      <div>
        <p className="font-bold text-[#0F172A]">{user.name}</p>
        <p className="text-sm text-slate-600">{user.email}</p>
        <p className="text-xs text-slate-500">{user.cricket_role}</p>
      </div>
      <Button
        onClick={toggleAdmin}
        disabled={loading}
        size="sm"
        variant={user.is_admin ? 'default' : 'outline'}
        className={user.is_admin ? 'bg-[#E63946] hover:bg-[#DC2626]' : ''}
        data-testid="toggle-admin-btn"
      >
        {user.is_admin ? 'Remove Admin' : 'Make Admin'}
      </Button>
    </div>
  );
}

export default AdminPanel;